import { useEffect, useState } from "react";
import api, { loadError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";

const OUTCOMES = {
  ok: { label: "Accesso riuscito", cls: "bg-emerald-50 text-emerald-700" },
  bad_password: { label: "Password errata", cls: "bg-red-50 text-red-700" },
  unknown_user: { label: "Utente inesistente", cls: "bg-red-50 text-red-700" },
  locked: { label: "Bloccato (troppi tentativi)", cls: "bg-orange-50 text-orange-700" },
};
const FILTERS = [["", "Tutti"], ["ok", "Riusciti"], ["bad_password", "Password errata"], ["unknown_user", "Utente inesistente"], ["locked", "Bloccati"]];

const shortUA = (ua = "") => {
  const b = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : ua ? "Altro" : "—";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${b} · ${os}` : b;
};

export const LoginAudit = () => {
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("");
  const load = (o = outcome) => api.get(`/users/login-audit${o ? `?outcome=${o}` : ""}`).then((r) => setRows(r.data)).catch(loadError);
  useEffect(() => { load(outcome); }, [outcome]); // eslint-disable-line
  const failed = rows.filter((r) => r.outcome !== "ok").length;
  return (
    <Card className="border-border shadow-sm overflow-hidden" data-testid="login-audit">
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-border">
        <h3 className="font-heading font-semibold mr-2">Registro accessi</h3>
        {FILTERS.map(([v, l]) => (
          <button key={v} type="button" data-testid={`audit-filter-${v || "all"}`} onClick={() => setOutcome(v)}
            className={`text-xs rounded-full px-3 py-1 border transition-colors ${outcome === v ? "bg-accent text-accent-foreground border-accent" : "border-border hover:bg-secondary"}`}>{l}</button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="audit-summary">
          {failed > 0 ? <ShieldAlert className="w-3.5 h-3.5 text-red-600" /> : <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />}
          {rows.length} voci · {failed} falliti · ultimi 90 giorni
        </span>
        <Button variant="ghost" size="sm" data-testid="audit-refresh" onClick={() => load()}><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50"><TableHead>Data e ora</TableHead><TableHead>Utente</TableHead><TableHead>Esito</TableHead><TableHead>IP</TableHead><TableHead>Browser</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => {
              const o = OUTCOMES[r.outcome] || { label: r.outcome, cls: "bg-secondary" };
              return (
                <TableRow key={r.id} data-testid={`audit-row-${r.id}`} data-outcome={r.outcome} className={r.outcome !== "ok" ? "bg-red-50/40" : ""}>
                  <TableCell className="text-sm whitespace-nowrap">{new Date(r.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" })}</TableCell>
                  <TableCell className="text-sm">{r.email}</TableCell>
                  <TableCell><Badge className={`${o.cls} border-0`}>{o.label}</Badge></TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{r.ip || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground" title={r.user_agent}>{shortUA(r.user_agent)}</TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nessun accesso registrato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
